"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import AlertMsg from "@/components/dialogs/common/AlertMsg";
import CampaignAccessSelect from "@/components/tenants/CampaignAccessSelect";
import SuccessModal from "@/components/tenants/SuccessModal";
import { createCampaignSchema } from "@/utils/schema/createCampaignSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCreateCampaignMutation } from "@/store/slices/apiSlice";
import AddExperienceDialog from "../experiences/AddExperienceDialog";
import { TenantSetupTracker } from "@/components/tenants/profile/SetupTracker";
import { focusFirstEditable } from "@/utils/focusFirstEditable";

const initAlertMsg = {
  open: false,
  label: "",
  msg: "",
  isError: false,
  isLoading: false,
  btnText: "",
  canClose: false,
};

const defaultFormValues = {
  campaignName: "",
  description: "",
  campaignAccessType: "INVITE_ONLY",
  allowedDomains: [],
  invitedEmails: [],
  // joinCode: "ACME-2026-01",
};

const CreateCampaignDialog = ({
  open,
  onClose,
  tenantDetails,
  onSuccess,
  tenantId,
  onAddExperience,
  steps,
  progress,
  showSetupSteps,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [domainInput, setDomainInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [alertMsg, setAlertMsg] = useState(initAlertMsg);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [openAddExperience, setOpenAddExperience] = useState(false);
  const [createCampaign] = useCreateCampaignMutation();
  const [createdCampaign1, setCreatedCampaign1] = useState(null);
  const form = useForm({
    resolver: zodResolver(createCampaignSchema),
    defaultValues: defaultFormValues,
  });

  const allowedDomains = form.watch("allowedDomains");
  const invitedEmails = form.watch("invitedEmails");

  const resetForm = () => {
    form.reset(defaultFormValues);
    setDomainInput("");
    setEmailInput("");
  };

  const handleDialogClose = () => {
    if (isLoading) return;

    resetForm();
    onClose?.();
  };

  const handleAlertClose = () => {
    setAlertMsg(initAlertMsg);
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    resetForm();
    onClose?.();
  };

  const handleAddAnotherCampaign = () => {
    setShowSuccessModal(false);
    resetForm();
  };

  const handleAddExperience = () => {
    setShowSuccessModal(false);
    resetForm();
    // onClose?.()
    // onAddExperience?.();
    setOpenAddExperience(true);
  };

  const addDomain = () => {
    const value = domainInput.trim().replace(",", "");

    if (!value) return;

    const currentDomains = form.getValues("allowedDomains") || [];

    if (!currentDomains.includes(value)) {
      form.setValue("allowedDomains", [...currentDomains, value], {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    setDomainInput("");
  };

  const removeDomain = (domain) => {
    const currentDomains = form.getValues("allowedDomains") || [];

    form.setValue(
      "allowedDomains",
      currentDomains.filter((item) => item !== domain),
      {
        shouldValidate: true,
        shouldDirty: true,
      },
    );
  };

  const addInvitedEmail = () => {
    const value = emailInput.trim().replace(",", "");

    if (!value) return;

    const currentEmails = form.getValues("invitedEmails") || [];

    if (!currentEmails.includes(value)) {
      form.setValue("invitedEmails", [...currentEmails, value], {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    setEmailInput("");
  };

  const removeInvitedEmail = (email) => {
    const currentEmails = form.getValues("invitedEmails") || [];

    form.setValue(
      "invitedEmails",
      currentEmails.filter((item) => item !== email),
      {
        shouldValidate: true,
        shouldDirty: true,
      },
    );
  };

  const onSubmit = async (values) => {
    try {
      setIsLoading(true);

      const payload = {
        tenantId: tenantId || tenantDetails?._id || tenantDetails?.id,
        campaignName: values.campaignName,
        description: values.description,
        campaignAccessType: values.campaignAccessType,
        allowedDomains: values.allowedDomains,
        invitedEmails:
          values.campaignAccessType === "INVITE_ONLY"
            ? values.invitedEmails
            : [],
        // joinCode: values.joinCode,
      };

      /*
        Replace this fake response with your real API call later: */

      const res = await createCampaign(payload).unwrap();

      if (res?.response?.statusEnum === 1) {
        setCreatedCampaign1(res?.response?.data);

        await onSuccess?.(res.response.data);
        setShowSuccessModal(true);
      } else {
        setAlertMsg({
          open: true,
          label: "Campaign Creation Failed",
          msg:
            err?.data?.response?.data?.[0] ||
            res?.response?.message ||
            "Unable to create campaign. Please try again.",
          isError: true,
          btnText: "Close",
          canClose: false,
        });
      }
    } catch (err) {
      setAlertMsg({
        open: true,
        label: "Campaign Creation Failed",
        msg:
          err?.data?.response?.data?.[0] ||
          err?.data?.response?.message ||
          "Something went wrong. Please try again.",
        isError: true,
        btnText: "Close",
        canClose: false,
      });

      console.error("Create campaign error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open && !showSuccessModal} onOpenChange={handleDialogClose}>
        <DialogContent
          className="
            w-[calc(100vw-32px)]
            max-w-[760px]
            max-h-[92vh]
            overflow-y-auto
            rounded-lg
            border
            bg-brand-10 border-brand-11
            px-5
            py-8
            text-neutral-100
            shadow-2xl
            sm:px-10
            lg:px-14
          "
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          // Prevent auto-focus when dialog opens
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            focusFirstEditable(e.currentTarget);
          }}
        >
          <DialogHeader className="mb-6 text-left">
            <DialogTitle className="text-left text-2xl font-bold text-[#67D8FF]">
              Create New Campaign
            </DialogTitle>

            <DialogDescription className="sr-only">
              Create new campaign form
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit, (errors) => {})}
              className="space-y-5"
            >
              <FormField
                control={form.control}
                name="campaignName"
                rules={{ required: "Campaign name is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-bold text-neutral-100">
                      Campaign Name
                    </FormLabel>

                    <FormControl>
                      <Input
                        placeholder="Enter campaign name (e.g. Cyber Awareness Q2)"
                        className="form-dark-input"
                        {...field}
                      />
                    </FormControl>

                    <FormMessage className="text-sm text-red-500" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-bold text-neutral-100">
                      Description
                    </FormLabel>

                    <p className="mb-3 text-sm text-neutral-100/60">
                      Enter a short description of this campaign
                    </p>

                    <FormControl>
                      <textarea
                        {...field}
                        className="
                          min-h-[90px]
                          w-full
                          rounded-md
                          border
                          border-[#14708A]
                          bg-[#151515]
                          px-4
                          py-3
                          text-sm
                          text-neutral-100
                          outline-none
                          placeholder:text-neutral-100/50
                          focus:border-[#00D7F2]
                        "
                      />
                    </FormControl>

                    <FormMessage className="text-sm text-red-500" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="allowedDomains"
                render={() => (
                  <FormItem>
                    <FormLabel className="text-sm font-bold text-neutral-100">
                      Invite users via adding domain addresses
                      {/* Invite users via uploading invite list or adding domain
                      addresses */}
                    </FormLabel>

                    <p className="mb-3 text-sm text-neutral-100/60">
                      Allow access to anyone signing in with an email address
                      from the domains below. Enter the complete domain
                      including the extension
                      <span className="font-bold text-[#B8F7FF]">
                        &nbsp;(e.g., bigbro.com, ginger.in)&nbsp;
                      </span>
                      and press Enter to add multiple domains.
                      {/* <span className="font-bold text-neutral-100/80">
                        specific domains
                      </span>
                      . Type domains and press Enter to add multiple domains. */}
                    </p>

                    <FormControl>
                      <div className="flex min-h-11 flex-wrap items-center gap-2 rounded-md border border-[#14708A] bg-[#151515] px-3 py-2">
                        {allowedDomains?.map((domain) => (
                          <span
                            key={domain}
                            className="flex items-center gap-2 rounded bg-[#293C79] px-2 py-1 text-xs font-semibold text-neutral-100"
                          >
                            {domain}

                            <button
                              type="button"
                              onClick={() => removeDomain(domain)}
                              className="text-neutral-100/80 hover:text-neutral-100"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}

                        <input
                          value={domainInput}
                          onChange={(e) => setDomainInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === ",") {
                              e.preventDefault();
                              addDomain();
                            }

                            if (
                              e.key === "Backspace" &&
                              !domainInput &&
                              allowedDomains?.length > 0
                            ) {
                              removeDomain(
                                allowedDomains[allowedDomains.length - 1],
                              );
                            }
                          }}
                          onBlur={addDomain}
                          placeholder={
                            allowedDomains?.length
                              ? ""
                              : "Enter domain and press Enter"
                          }
                          className="min-w-[150px] flex-1 bg-transparent text-sm text-neutral-100 outline-none placeholder:text-neutral-100/50"
                        />
                      </div>
                    </FormControl>

                    {/* <FormMessage className="text-sm text-red-500" /> */}
                    {Array.isArray(form.formState.errors.allowedDomains) &&
                      form.formState.errors.allowedDomains.map((err, index) => {
                        if (!err) return null;

                        return (
                          <p key={index} className="text-sm text-red-500">
                            {err.message}
                          </p>
                        );
                      })}
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="campaignAccessType"
                rules={{ required: "Campaign access type is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-bold text-neutral-100">
                      Campaign Access Type
                    </FormLabel>

                    <FormControl>
                      <CampaignAccessSelect
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>

                    <FormMessage className="text-sm text-red-500" />
                  </FormItem>
                )}
              />
              {/* 
              {form.watch("campaignAccessType") === "INVITE_ONLY" && (
                <FormField
                  control={form.control}
                  name="invitedEmails"
                  render={() => (
                    <FormItem>
                      <FormLabel className="text-sm font-bold text-neutral-100">
                        Invited Emails
                      </FormLabel>

                      <p className="mb-3 text-sm text-neutral-100/60">
                        Type email addresses and press Enter to add multiple
                        emails.
                      </p>

                      <FormControl>
                        <div className="flex min-h-11 flex-wrap items-center gap-2 rounded-md border border-[#14708A] bg-[#151515] px-3 py-2">
                          {invitedEmails?.map((email) => (
                            <span
                              key={email}
                              className="flex items-center gap-2 rounded bg-[#293C79] px-2 py-1 text-xs font-semibold text-neutral-100"
                            >
                              {email}

                              <button
                                type="button"
                                onClick={() => removeInvitedEmail(email)}
                                className="text-neutral-100/80 hover:text-neutral-100"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          ))}

                          <input
                            value={emailInput}
                            onChange={(e) => setEmailInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === ",") {
                                e.preventDefault();
                                addInvitedEmail();
                              }

                              if (
                                e.key === "Backspace" &&
                                !emailInput &&
                                invitedEmails?.length > 0
                              ) {
                                removeInvitedEmail(
                                  invitedEmails[invitedEmails.length - 1],
                                );
                              }
                            }}
                            onBlur={addInvitedEmail}
                            placeholder={
                              invitedEmails?.length
                                ? ""
                                : "Enter email and press Enter"
                            }
                            className="min-w-[180px] flex-1 bg-transparent text-sm text-neutral-100 outline-none placeholder:text-neutral-100/50"
                          />
                        </div>
                      </FormControl>

                      {Array.isArray(form.formState.errors.invitedEmails) &&
                        form.formState.errors.invitedEmails.map(
                          (err, index) => {
                            if (!err) return null;

                            return (
                              <p key={index} className="text-sm text-red-500">
                                {err.message}
                              </p>
                            );
                          },
                        )}
                    </FormItem>
                  )}
                />
              )} */}

              {/* <FormField
                control={form.control}
                name="joinCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-bold text-neutral-100">
                      Join Code Auto-generated
                    </FormLabel>

                    <FormControl>
                      <Input
                        readOnly
                        className="form-dark-input font-bold text-[#00D7F2]"
                        {...field}
                      />
                    </FormControl>

                    <FormMessage className="text-sm text-red-500" />
                  </FormItem>
                )}
              /> */}

              <DialogFooter className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDialogClose}
                  disabled={isLoading || form.formState.isSubmitting}
                  className="
                    h-11
                    w-full
                    border-[#607A9B]
                    bg-[#223957]
                    px-8
                    font-bold
                    text-neutral-100
                    hover:bg-[#2B466B]
                    hover:text-neutral-100
                    sm:w-auto
                  "
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  disabled={isLoading || form.formState.isSubmitting}
                  className="
                    h-11
                    w-full
                    bg-brand-21
                    px-8
                    font-bold
                    text-neutral-100
                    hover:bg-brand-22
                    sm:w-auto
                  "
                >
                  {isLoading || form.formState.isSubmitting
                    ? "Creating..."
                    : "Create Campaign"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <AlertMsg
        label={alertMsg.label}
        msg={alertMsg.msg}
        open={alertMsg.open}
        onClose={handleAlertClose}
        confirmAction={handleAlertClose}
        isLoading={false}
        isError={alertMsg.isError}
        canClose={alertMsg.canClose}
        confirmationBtnText={alertMsg.btnText}
      />

      <SuccessModal
        open={showSuccessModal}
        onClose={handleSuccessClose}
        // onAddExperience={handleAddExperience}
        // onAddCampaign={handleAddAnotherCampaign}
        title="Organisation Created Successfully"
        description="The organisation has been set up successfully. You can add experiences or create more campaigns now, or do it later from the organisation profile."
        primaryButtonText="Add Experience to this Campaign"
        secondaryButtonText="Close"
        onPrimaryClick={handleAddExperience}
        onSecondaryClick={handleSuccessClose}
        onPrimaryClick1={() => {
          setShowCampaignSuccess(false);
          // setSelectedCampaign(createdCampaign);
          setOpenAddExperience(true);
        }}
        // onSecondaryClick={() => {
        //   setShowCampaignSuccess(false);
        // }}
        showIcon={!showSetupSteps}
        iconType="success"
      >
        <TenantSetupTracker
          // showSteps={showSetupSteps}
          steps={showSetupSteps ? steps : []}
          progress={showSetupSteps ? progress : undefined}
        />
      </SuccessModal>

      <AddExperienceDialog
        open={openAddExperience}
        onClose={() => {
          setOpenAddExperience(false);
          setShowSuccessModal(false);
          onClose?.();
        }}
        tenantDetails={tenantDetails}
        tenantName={tenantDetails?.orgName || tenantDetails?.name || ""}
        defaultCampaign={createdCampaign1?.campaignName || ""}
        campaignId={createdCampaign1?._id || ""}
      />
    </>
  );
};

export default CreateCampaignDialog;
